import { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Icon } from "@evertrue/et-components";
import { useDropzone } from "react-dropzone";
import { useIdentity } from "base/identity-resolver";
import {
  setBusinessLogo,
  setDefaultButtonOptions,
  setDefaultLogoOptions,
  getUserDefaultSettings,
} from "../../entities/helpers/thankview-helper";
import useUserAuthentication from "./hooks/useUserAuthentication";

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const FileUploadWithPreview = ({ businessId, clientId, clientSecret, onFileSelect }) => {
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const handleFile = useCallback(
    (file) => {
      if (file) {
        onFileSelect(file);
        const reader = new FileReader();
        reader.onload = async (e) => {
          setPreview(e.target.result);
          try {
            const formData = new FormData();
            formData.append("logo_image", file);
            await setBusinessLogo(businessId, formData, clientId, clientSecret);
            console.log("Logo updated successfully");
          } catch (error) {
            console.error("Failed to update logo:", error);
          }
        };
        reader.readAsDataURL(file);
        return file;
      }
    },
    [businessId, clientId, clientSecret, onFileSelect]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      handleFile(acceptedFiles[0]);
    },
    [handleFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpg", ".jpeg", ".png", ".svg"] },
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    onFileSelect(null);
  };

  return (
    <div
      className="upload-container"
      {...getRootProps()}
      tabIndex="0"
      role="button"
      aria-label="Upload logo file"
      onKeyDown={handleKeyDown}
    >
      <input {...getInputProps()} ref={fileInputRef} aria-label="File input" />
      {preview ? (
        <div className="preview-container">
          <img className="preview-image" src={preview} alt="Logo preview" />
          <button className="remove-button" onClick={handleRemove} aria-label="Remove uploaded file">
            ✖
          </button>
        </div>
      ) : (
        <>
          <Icon className="upload-icon" aria-hidden="true" iconKit="tv" icon="uploadvector"></Icon>
          <p className="upload-text">
            {isDragActive ? (
              "Drop the file here"
            ) : (
              <>
                Drag and Drop file here or <span className="uplgo backoad-text-underline">Choose file</span>
              </>
            )}{" "}
          </p>
        </>
      )}
      <p className="supported-formats" aria-live="polite">
        Supported formats: jpg, png, svg | Maximum size: 5MB
      </p>
    </div>
  );
};
const SettingToggle = ({ label, checked, onChange }) => {
  return (
    <div className="default-settings-container default-setting-subheader-font settings-border toggle-container">
      <span>{label}</span>
      <div className="switch-container">
        <label className="tv-switch">
          <input
            type="checkbox"
            aria-checked={checked ? "true" : "false"}
            aria-labelledby="toggle-switch"
            checked={checked}
            onChange={onChange}
          />
          <span className="tv-slider round"></span>
        </label>
      </div>
    </div>
  );
};
const ColorPicker = ({ color, trackColor, onColorChange, onTrackColorChange }) => (
  <div class="color-picker">
    <div class="player-color">
      <label htmlFor="playerColor">Player Color</label>
      <input type="color" id="playerColor" name="playerColor" value={color} onChange={onColorChange} />
    </div>
    <div class="track-color">
      <label htmlFor="trackColor">Track Color</label>
      <input type="color" id="trackColor" name="trackColor" value={trackColor} onChange={onTrackColorChange} />
    </div>
  </div>
);

const ThankviewDefaultSettings = () => {
  // Enhancements for color customization
  // const { color, trackColor, handleColorChange, handleTrackColorChange } = useRecorder();
  const { org } = useIdentity();
  const { clientId } = useUserAuthentication();
  const [userDefaults, setUserDefaults] = useState(null);
  console.log(clientId);
  let client_id = clientId && clientId.data.client_id;
  let client_secret = clientId && clientId.data.client_secret;
  const fetchUserDefaultSettings = useCallback(async () => {
    if (!clientId?.data?.client_id || !clientId?.data?.client_secret) {
      console.log("Client ID or secret is missing");
      return;
    }

    try {
      let response = await getUserDefaultSettings(client_id, client_secret);
      console.log("API response:", response);
      setUserDefaults(response.data);
    } catch (error) {
      console.error("Error fetching user default settings:", error);
    }
  }, [client_id, client_secret, clientId]);

  useEffect(() => {
    fetchUserDefaultSettings();
  }, [fetchUserDefaultSettings]);
  const defaultToTrue = (obj) => {
    const newObj = { ...obj };
    for (const key in newObj) {
      if (typeof newObj[key] === "boolean" || newObj[key] === undefined) {
        newObj[key] = newObj[key] !== false;
      }
    }
    return newObj;
  };

  const [videoOptions, setVideoOptions] = useState(() => defaultToTrue(userDefaults));
  const handleFileSelect = (file) => {
    setVideoOptions((prev) => ({
      ...prev,
      logo_file: file,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Input changed:", name, value);
    setVideoOptions((prevOptions) => ({
      ...prevOptions,
      [name]: value,
    }));
  };
  const updateUIImmediately = (setting) => {
    setVideoOptions((prevOptions) => ({
      ...prevOptions,
      [setting]: !prevOptions[setting],
    }));
  };

  const debouncedApiCall = debounce(async (setting, newValue) => {
    if (setting === "logo_enabled") {
      await setDefaultLogoOptions({ logo_enabled: newValue }, clientId.data.client_id, clientId.data.client_secret);
    } else if (["button_enabled", "show_in_email", "button_text", "button_url"].includes(setting)) {
      await setDefaultButtonOptions(
        {
          ...videoOptions,
          [setting]: newValue,
        },
        clientId.data.client_id,
        clientId.data.client_secret
      );
    }
  }, 1000);

  const toggleSetting = useCallback(
    debounce(async (setting) => {
      const newValue = !videoOptions[setting];
      updateUIImmediately(setting);
      debouncedApiCall(setting, newValue);
    }, 300),
    [videoOptions, clientId] // Add any other dependencies
  );

  useEffect(() => {
    const updateButtonOptions = async () => {
      if (videoOptions.button_enabled) {
        await setDefaultButtonOptions(
          {
            button_enabled: videoOptions.button_enabled,
            show_in_email: videoOptions.show_in_email,
            button_text: videoOptions.button_text,
            button_url: videoOptions.button_url,
          },
          clientId ? clientId.data.client_id : "",
          clientId ? clientId.data.client_secret : ""
        );
      }
    };

    updateButtonOptions();
  }, [
    clientId,
    videoOptions.button_enabled,
    videoOptions.button_text,
    videoOptions.button_url,
    videoOptions.show_in_email,
  ]);

  return (
    <div>
      <Card headingText="Landing Page Settings" padding="16px" className="default-settings-font">
        <div id="landing-page-settings">
          <div id="landing-page-settings-subsection">
            <SettingToggle
              label="Show Logo on Landing Page"
              checked={videoOptions.logo_enabled}
              onChange={() => toggleSetting("logo_enabled")}
            />
            {videoOptions.logo_enabled && (
              <div>
                <FileUploadWithPreview
                  businessId={org.id}
                  clientId={clientId && clientId.data.client_id}
                  clientSecret={clientId && clientId.data.client_secret}
                  onFileSelect={handleFileSelect}
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card headingText="Call-To-Action Button Settings" padding="16px" className="default-settings-font">
        <div id="email-settings">
          <SettingToggle
            label="Toggle Call-To-Action Button"
            checked={videoOptions.button_enabled}
            onChange={() => toggleSetting("button_enabled")}
          />
          {videoOptions.show_in_email && (
            <>
              <div className="tv-input-box">
                <input
                  label="Button Text"
                  type="text"
                  value={videoOptions.button_text || ""}
                  onChange={handleInputChange}
                  className="button-input"
                  name="button_text"
                  placeholder="Enter Button Text"
                />
                <input
                  label="Button Link"
                  type="text"
                  className="button-input"
                  value={videoOptions.button_url || ""}
                  onChange={handleInputChange}
                  name="button_url"
                  placeholder="Enter Button Link URL"
                />
                <div className="info-container settings-border">
                  <i className="gt-icon gt-icon-info tooltip-icon" aria-hidden="true" />
                  <p className="info-text">Ensure URL contains https://</p>
                </div>
              </div>
            </>
          )}
          <SettingToggle
            label="Show Button in Email"
            checked={videoOptions.show_in_email}
            onChange={() => toggleSetting("show_in_email")}
          />{" "}
          {/* <SettingToggle
            label="Show Button on Landing Page"
            checked={videoOptions.show_in_email}
            onChange={() => toggleSetting("show_in_email")}
          /> */}
        </div>
      </Card>
      {/* <Card headingText="Email Settings" padding="16px" className="default-settings-font">
        <div id="email-settings">
          <SettingToggle
            label="Animated Thumbnail"
            checked={videoOptions.show_animated_thumbnail}
            onChange={() => toggleSetting("show_animated_thumbnail")}
          />
        </div>
      </Card> */}
      {/* Hide for Future
       <Card headingText="Video Settings" padding="16px" className="default-settings-font">
        <SettingToggle
          label="Enable Closed Captions on Video"
          checked={videoOptions.enable_closed_captions}
          onChange={() => toggleSetting("enable_closed_captions")}
        />
        <ColorPicker
          color={color}
          trackColor={trackColor}
          onColorChange={handleColorChange}
          onTrackColorChange={handleTrackColorChange}
        />
      </Card> */}
    </div>
  );
};

SettingToggle.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

FileUploadWithPreview.propTypes = {
  onFileSelect: PropTypes.func,
  businessId: PropTypes.string,
  clientId: PropTypes.string,
  clientSecret: PropTypes.string,
};

ColorPicker.propTypes = {
  color: PropTypes.string.isRequired,
  trackColor: PropTypes.string.isRequired,
  onColorChange: PropTypes.func.isRequired,
  onTrackColorChange: PropTypes.func.isRequired,
};

export default ThankviewDefaultSettings;
